import type {
  SiteArtist,
  SiteArtistDetail,
  SiteEvent,
  SiteEventDetail,
  SiteEventFaqItem,
  SiteFaqItem,
  SiteLinkItem,
  SiteRelease,
  SiteShopItem,
} from '../api/public'
import type { SiteChrome } from '../api/site-chrome'
import { FALLBACK_SITE_CHROME } from '../api/site-chrome'
import { cmsImageUrl, cmsImageVariantUrl, CMS_STYLE_WIDTH, type CmsImageStyle } from './cms-image'
import { absoluteUrl, LINKS_ORIGIN, SITE_ORIGIN } from './site-origin'
import { stripHtmlToText } from './strip-html'

export type JsonLd = Record<string, unknown>

const CTX = 'https://schema.org'

export const BNKR_ORG_ID = `${SITE_ORIGIN}/#organization`
export const BNKR_WEBSITE_ID = `${SITE_ORIGIN}/#website`

const DEFAULT_SAME_AS = [
  'https://www.instagram.com/bnkr.ch/',
  'https://soundcloud.com/bnkrlabel',
  'https://open.spotify.com/user/bnkr-digital-agency',
  LINKS_ORIGIN,
]

/**
 * Structured-data images are consumed by crawlers and link unfurlers, several
 * of which still handle WebP poorly — so they get a JPEG derivative rather than
 * the WebP used on the page (and never the multi-MB original).
 */
function img(url: string | null | undefined, size: CmsImageStyle = 'lg'): string | undefined {
  const u = url ? cmsImageVariantUrl(url, CMS_STYLE_WIDTH[size], 'jpg') ?? cmsImageUrl(url, size) ?? url : null
  return u ? absoluteUrl(u) : undefined
}

function text(html: string | null | undefined, max = 500): string | undefined {
  const t = stripHtmlToText(html, max)
  return t || undefined
}

function sameAsFromChrome(chrome?: SiteChrome): string[] {
  const links = chrome?.footerColumns?.flatMap((c) => c.links) ?? FALLBACK_SITE_CHROME.footerColumns.flatMap((c) => c.links)
  const urls = links
    .filter((l) => l.external && /^https?:\/\//i.test(l.href))
    .map((l) => l.href)
  return [...new Set([...DEFAULT_SAME_AS, ...urls])]
}

function postalAddress() {
  return {
    '@type': 'PostalAddress',
    addressLocality: 'Weinfelden',
    addressRegion: 'TG',
    addressCountry: 'CH',
  }
}

function eventStatus(ev: SiteEvent): string {
  if (ev.displayStatus === 'cancelled') return `${CTX}/EventCancelled`
  if (ev.displayStatus === 'past') return `${CTX}/EventScheduled`
  return `${CTX}/EventScheduled`
}

function eventLocation(ev: SiteEvent) {
  const name = ev.venueName || ev.locationLabel
  if (!name && !ev.addressLine) return undefined
  return {
    '@type': 'Place',
    name: name ?? undefined,
    address: ev.addressLine
      ? {
          '@type': 'PostalAddress',
          streetAddress: ev.addressLine,
          addressLocality: ev.locationLabel ?? undefined,
          addressCountry: 'CH',
        }
      : postalAddress(),
  }
}

function performers(ev: SiteEventDetail | SiteEvent): JsonLd[] {
  const out: JsonLd[] = []
  const detail = ev as SiteEventDetail
  for (const a of detail.lineupArtists ?? []) {
    out.push({
      '@type': 'Person',
      name: a.name,
      url: absoluteUrl(`/artists/${a.slug}`),
    })
  }
  for (const g of detail.guestArtists ?? ev.guestArtistsJson ?? []) {
    if (!g.name?.trim()) continue
    out.push({
      '@type': 'Person',
      name: g.name,
      url: g.websiteUrl || g.spotifyUrl || undefined,
      sameAs: [g.spotifyUrl, g.soundcloudUrl, g.websiteUrl].filter(Boolean),
    })
  }
  if (ev.lineupText?.trim() && out.length === 0) {
    out.push({ '@type': 'PerformingGroup', name: ev.lineupText.trim() })
  }
  return out
}

function eventOffers(ev: SiteEvent, ticketPageUrl?: string): JsonLd[] | undefined {
  const shop = ticketPageUrl || ev.ticketUrl
  if (!shop) return undefined
  const url = /^https?:\/\//i.test(shop) ? shop : absoluteUrl(shop)
  return [
    {
      '@type': 'Offer',
      url,
      availability: `${CTX}/InStock`,
      validFrom: ev.startsAt,
      priceCurrency: 'CHF',
      category: 'primary',
      seller: { '@id': BNKR_ORG_ID },
    },
  ]
}

/** Organization — injected site-wide from Layout. */
export function schemaOrganization(opts?: { logoUrl?: string | null; chrome?: SiteChrome }): JsonLd {
  const chrome = opts?.chrome ?? FALLBACK_SITE_CHROME
  const logo = img(opts?.logoUrl ?? null, 'md')
  return {
    '@type': 'Organization',
    '@id': BNKR_ORG_ID,
    name: 'BNKR',
    alternateName: ['BNKR Bunker Events', 'Bunker Events', chrome.brandSub ?? 'Bunker-Events'].filter(Boolean),
    legalName: 'BNKR Bunker Verein',
    url: SITE_ORIGIN,
    logo: logo ? { '@type': 'ImageObject', url: logo } : undefined,
    image: logo,
    email: chrome.contactEmail ?? 'info@bnkr.ch',
    address: postalAddress(),
    areaServed: { '@type': 'Country', name: 'Switzerland' },
    sameAs: sameAsFromChrome(chrome),
    knowsAbout: ['Electronic music events', 'Nightlife', 'Music label'],
  }
}

export function schemaWebSite(): JsonLd {
  return {
    '@type': 'WebSite',
    '@id': BNKR_WEBSITE_ID,
    url: SITE_ORIGIN,
    name: 'BNKR',
    alternateName: 'BNKR Bunker Events',
    description: 'Bunker Events, Label und Verein — Events, Releases und Artists aus der Ostschweiz.',
    publisher: { '@id': BNKR_ORG_ID },
    inLanguage: 'de-CH',
    isPartOf: { '@id': BNKR_ORG_ID },
  }
}

export function schemaBreadcrumb(items: Array<{ name: string; path: string }>, pagePath?: string): JsonLd {
  const base = absoluteUrl(pagePath ?? items[items.length - 1]?.path ?? '/')
  return {
    '@type': 'BreadcrumbList',
    '@id': `${base}#breadcrumb`,
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  }
}

export function schemaWebPage(opts: {
  path: string
  name: string
  description?: string | null
  pageType?: string
  image?: string | null
  breadcrumb?: JsonLd
  mainEntity?: JsonLd | JsonLd[]
  about?: JsonLd | JsonLd[]
  noindex?: boolean
}): JsonLd {
  const url = absoluteUrl(opts.path)
  const image = img(opts.image, 'lg')
  return {
    '@type': opts.pageType ?? 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: opts.name,
    description: opts.description?.trim() || undefined,
    inLanguage: 'de-CH',
    isPartOf: { '@id': BNKR_WEBSITE_ID },
    publisher: { '@id': BNKR_ORG_ID },
    primaryImageOfPage: image ? { '@type': 'ImageObject', url: image } : undefined,
    image,
    breadcrumb: opts.breadcrumb ? { '@id': `${url}#breadcrumb` } : undefined,
    mainEntity: opts.mainEntity,
    about: opts.about,
  }
}

export function schemaMusicEvent(
  ev: SiteEvent | SiteEventDetail,
  opts?: { detailPath?: string; ticketUrl?: string | null },
): JsonLd {
  const path = opts?.detailPath ?? `/events/${ev.slug}`
  const url = absoluteUrl(path)
  const images = [img(ev.featuredImageUrl, 'lg'), ...(ev.galleryUrls ?? []).map((u) => img(u, 'lg'))].filter(
    Boolean,
  ) as string[]
  const description = ev.summary?.trim() || text(ev.bodyHtml, 320)
  const end = ev.endsAt ?? ev.startsAt

  return {
    '@type': 'MusicEvent',
    '@id': `${url}#event`,
    name: ev.title,
    description,
    url,
    startDate: ev.startsAt,
    endDate: end,
    eventStatus: eventStatus(ev),
    eventAttendanceMode: `${CTX}/OfflineEventAttendanceMode`,
    inLanguage: 'de-CH',
    image: images.length ? images : undefined,
    location: eventLocation(ev),
    organizer: { '@id': BNKR_ORG_ID },
    performer: (() => {
      const p = performers(ev)
      return p.length ? p : undefined
    })(),
    offers: eventOffers(ev, opts?.ticketUrl ?? undefined),
  }
}

export function schemaEventList(events: SiteEvent[], listPath = '/events'): JsonLd {
  return {
    '@type': 'ItemList',
    name: 'BNKR Events',
    url: absoluteUrl(listPath),
    numberOfItems: events.length,
    itemListElement: events.map((ev, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: absoluteUrl(`/events/${ev.slug}`),
      item: {
        '@type': 'MusicEvent',
        name: ev.title,
        startDate: ev.startsAt,
        url: absoluteUrl(`/events/${ev.slug}`),
        image: img(ev.featuredImageUrl, 'md'),
        eventStatus: eventStatus(ev),
      },
    })),
  }
}

export function schemaEventFaq(faq: SiteEventFaqItem[]): JsonLd {
  return {
    '@type': 'FAQPage',
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: text(item.answerHtml, 2000) ?? item.question,
      },
    })),
  }
}

export function schemaFaqPage(items: SiteFaqItem[], path = '/faq'): JsonLd {
  return {
    '@type': 'FAQPage',
    '@id': `${absoluteUrl(path)}#faq`,
    url: absoluteUrl(path),
    inLanguage: 'de-CH',
    isPartOf: { '@id': BNKR_WEBSITE_ID },
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: text(item.answerHtml, 2000) ?? item.question,
      },
    })),
  }
}

export function schemaPersonArtist(artist: SiteArtist | SiteArtistDetail, path: string): JsonLd {
  const url = absoluteUrl(path)
  const portrait = img(artist.portraitImageUrl, 'lg')
  const sameAs = [artist.websiteUrl, artist.spotifyUrl, artist.soundcloudUrl, artist.bandcampUrl, artist.mixcloudUrl].filter(
    Boolean,
  ) as string[]
  return {
    '@type': 'Person',
    '@id': `${url}#person`,
    name: artist.name,
    url,
    description: text(artist.bioHtml, 400),
    image: portrait,
    sameAs: sameAs.length ? sameAs : undefined,
    knowsAbout: 'Electronic music',
  }
}

export function schemaArtistList(artists: SiteArtist[]): JsonLd {
  return {
    '@type': 'ItemList',
    name: 'BNKR Artists',
    url: absoluteUrl('/artists'),
    numberOfItems: artists.length,
    itemListElement: artists.map((a, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: absoluteUrl(`/artists/${a.slug}`),
      item: {
        '@type': 'Person',
        name: a.name,
        url: absoluteUrl(`/artists/${a.slug}`),
        image: img(a.portraitImageUrl, 'thumb'),
      },
    })),
  }
}

export function schemaMusicAlbum(release: SiteRelease, artistName?: string): JsonLd {
  const url = absoluteUrl(`/releases#${release.slug}`)
  const cover = img(release.coverImageUrl, 'md')
  return {
    '@type': 'MusicAlbum',
    name: release.title,
    url: release.spotifyUrl || release.soundcloudUrl || url,
    datePublished: release.releaseDate ?? undefined,
    genre: 'Electronic',
    byArtist: artistName ? { '@type': 'Person', name: artistName } : { '@id': BNKR_ORG_ID },
    image: cover,
    sameAs: [release.spotifyUrl, release.soundcloudUrl, release.mp3Url].filter(Boolean),
    albumReleaseType: release.releaseType,
  }
}

export function schemaReleaseList(releases: SiteRelease[]): JsonLd {
  return {
    '@type': 'ItemList',
    name: 'BNKR Releases',
    url: absoluteUrl('/releases'),
    numberOfItems: releases.length,
    itemListElement: releases.map((r, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: schemaMusicAlbum(r),
    })),
  }
}

export function schemaProductItem(item: SiteShopItem): JsonLd {
  return {
    '@type': 'Product',
    name: item.title,
    description: item.subtitle ?? undefined,
    image: img(item.imageUrl, 'md'),
    url: item.linkUrl || undefined,
    brand: { '@id': BNKR_ORG_ID },
  }
}

export function schemaShopList(items: SiteShopItem[]): JsonLd {
  return {
    '@type': 'ItemList',
    name: 'BNKR Shop',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: schemaProductItem(item),
    })),
  }
}

export function schemaTeamMember(member: { displayName: string; photoUrl: string | null }, path: string): JsonLd {
  return {
    '@type': 'Person',
    name: member.displayName,
    image: img(member.photoUrl, 'thumb'),
    worksFor: { '@id': BNKR_ORG_ID },
    url: absoluteUrl(path),
  }
}

export function schemaContactPage(path = '/contact'): JsonLd[] {
  const url = absoluteUrl(path)
  const chrome = FALLBACK_SITE_CHROME
  return [
    {
      '@type': 'ContactPage',
      '@id': `${url}#webpage`,
      url,
      name: 'Kontakt — BNKR',
      description: 'Kontaktformular für Booking, Events, DJ Submissions und Vereinsanfragen.',
      inLanguage: 'de-CH',
      isPartOf: { '@id': BNKR_WEBSITE_ID },
      publisher: { '@id': BNKR_ORG_ID },
    },
    {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: chrome.contactEmail ?? 'info@bnkr.ch',
      areaServed: 'CH',
      availableLanguage: ['German', 'English'],
      url,
    },
  ]
}

export function schemaAboutPage(opts: {
  path?: string
  title: string
  description?: string
  heroImage?: string | null
  team?: Array<{ displayName: string; photoUrl: string | null }>
  faq?: SiteFaqItem[]
}): JsonLd[] {
  const path = opts.path ?? '/about'
  const url = absoluteUrl(path)
  const nodes: JsonLd[] = [
    {
      '@type': 'AboutPage',
      '@id': `${url}#webpage`,
      url,
      name: opts.title,
      description: opts.description,
      inLanguage: 'de-CH',
      isPartOf: { '@id': BNKR_WEBSITE_ID },
      publisher: { '@id': BNKR_ORG_ID },
      primaryImageOfPage: img(opts.heroImage, 'lg')
        ? { '@type': 'ImageObject', url: img(opts.heroImage, 'lg') }
        : undefined,
      about: { '@id': BNKR_ORG_ID },
    },
  ]
  if (opts.team?.length) {
    nodes.push({
      '@type': 'ItemList',
      name: 'BNKR Team',
      itemListElement: opts.team.map((m, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: schemaTeamMember(m, `${path}#team`),
      })),
    })
  }
  if (opts.faq?.length) nodes.push(schemaFaqPage(opts.faq, path))
  return nodes
}

export function schemaTicketPage(
  ev: SiteEventDetail,
  shopUrl: string,
): JsonLd[] {
  const ticketPath = `/events/${ev.slug}/tickets`
  const detailPath = `/events/${ev.slug}`
  const ticketAbs = absoluteUrl(ticketPath)
  const event = schemaMusicEvent(ev, { detailPath, ticketUrl: shopUrl })
  event.offers = [
    {
      '@type': 'Offer',
      url: shopUrl,
      availability: `${CTX}/InStock`,
      validFrom: ev.startsAt,
      priceCurrency: 'CHF',
      category: 'primary',
      seller: { '@id': BNKR_ORG_ID },
      description: `Tickets für ${ev.title}`,
    },
  ]

  return [
    schemaBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Events', path: '/events' },
      { name: ev.title, path: detailPath },
      { name: 'Tickets', path: ticketPath },
    ]),
    {
      '@type': 'WebPage',
      '@id': `${ticketAbs}#webpage`,
      url: ticketAbs,
      name: `Tickets — ${ev.title}`,
      description: ev.summary?.trim() || `Tickets und Checkout für ${ev.title} bei BNKR.`,
      inLanguage: 'de-CH',
      isPartOf: { '@id': BNKR_WEBSITE_ID },
      mainEntity: { '@id': `${absoluteUrl(detailPath)}#event` },
      significantLink: shopUrl,
      potentialAction: {
        '@type': 'BuyAction',
        target: shopUrl,
        name: 'Tickets kaufen',
      },
    },
    event,
  ]
}

export function schemaLinktreeProfile(opts: {
  chrome: { profileLabel: string; profileUrl: string; footerText?: string }
  links: SiteLinkItem[]
}): JsonLd[] {
  const profileUrl = opts.chrome.profileUrl?.trim() || SITE_ORIGIN
  return [
    {
      '@type': 'ProfilePage',
      '@id': `${LINKS_ORIGIN}/#profile`,
      url: LINKS_ORIGIN,
      name: opts.chrome.profileLabel,
      description: opts.chrome.footerText,
      inLanguage: 'de-CH',
      isPartOf: { '@id': BNKR_ORG_ID },
      mainEntity: {
        '@type': 'Person',
        name: opts.chrome.profileLabel,
        url: profileUrl,
        sameAs: DEFAULT_SAME_AS,
      },
    },
    {
      '@type': 'ItemList',
      name: 'BNKR Links',
      itemListElement: opts.links.map((link, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: link.label,
        url: link.url,
      })),
    },
  ]
}

/** Merge nodes into one JSON-LD document with @graph. */
export function schemaGraph(...nodes: JsonLd[]): JsonLd {
  const graph = nodes.flat().filter(Boolean)
  return { '@context': CTX, '@graph': graph }
}
